import { PortalPageContent, PortalPageContentHeader } from "@fero/layouts";
import { useTradeSalesStore } from "@fero/stores";
import { AppRoutes } from "@fero/utils";
import LoadingButton from "@mui/lab/LoadingButton";
import { Grid, Stack, Typography } from "@mui/material";
import {
  type PaymentTermsPeriod,
  type TradeSaleSettlementMethod,
  valuesTradeSaleSettlementMethod,
} from "adl-gen/ferovinum/app/db";
import {
  type PaymentTermsFormValues,
  PaymentTermsInput,
  paymentTermsValidationSchema,
} from "components/library/widgets/payment-terms-input";
import { BaseWizardCard } from "components/library/widgets/wizard-card/base-wizard-card";
import { Loader } from "components/widgets/loader/loader";
import { useFormik } from "formik";
import { useEffect, useMemo } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { ensure } from "utils/null-and-undefined-utils";
import { isNumber } from "utils/ts-utils";
import { isLoaded } from "utils/utility-types";
import { object, string } from "yup";
import { PurchaseRequestFlowStepper } from "../../../../widgets/flow-stepper/purchase-request-flow-stepper";
import type { PurchaseRequestFlowState } from "../purchase-request-types";
import { usePurchaseRequestPreview } from "../use-purchase-request-preview";
import { OrderSummaryPreviewCard, SettlementOptionsCard } from "./components";

const PageHeader = ({
  purchaserName,
  hasProductionPlan,
}: {
  purchaserName: string;
  hasProductionPlan: boolean;
}) => {
  const { options } = useTradeSalesStore();
  return (
    <PortalPageContentHeader
      variant="split"
      title={
        <Stack>
          <Typography variant={"h4"}>
            {options.isEditMode ? "Edit Trade Sale" : "New Trade Sale"}
          </Typography>
          <Typography>from {purchaserName}</Typography>
        </Stack>
      }
      right={
        <PurchaseRequestFlowStepper activeStep={hasProductionPlan ? 3 : 2} />
      }
    />
  );
};
const getPaymentTermsPeriod = (
  paymentTermsType: PaymentTermsPeriod["kind"],
  numDaysAfterDelivery: number,
  numMonthsFollowingDelivery: number,
) => {
  const value =
    paymentTermsType === "daysAfterCollection"
      ? numDaysAfterDelivery
      : numMonthsFollowingDelivery;
  return {
    kind: paymentTermsType,
    value: isNumber(value) ? value : 0,
  };
};
const buildNewFlowState = (
  purchaseRequestFlowState: PurchaseRequestFlowState,
  advanceCashUponDelivery: boolean,
  settlementOption: TradeSaleSettlementMethod,
  paymentTermsType: PaymentTermsPeriod["kind"],
  numDaysAfterDelivery: number,
  numMonthsFollowingDelivery: number,
): PurchaseRequestFlowState => {
  return {
    ...purchaseRequestFlowState,
    settlement: {
      advanceCashUponDelivery,
      settlementOption: settlementOption,
      paymentTermsPeriod: getPaymentTermsPeriod(
        paymentTermsType,
        numDaysAfterDelivery,
        numMonthsFollowingDelivery,
      ),
    },
  };
};
interface PaymentPageFormValues extends PaymentTermsFormValues {
  settlementOption?: TradeSaleSettlementMethod;
  advanceCashUponDelivery?: boolean;
}

export const OrganisationPurchaserRequestSettlementPage = () => {
  const history = useHistory();
  const location = useLocation();
  const purchaseRequestFlowState = ensure(
    location.state,
  ) as PurchaseRequestFlowState;
  const purchaser = ensure(purchaseRequestFlowState.setup?.purchaser);
  const { options } = useTradeSalesStore();
  const form = useFormik<PaymentPageFormValues>({
    initialValues: {
      advanceCashUponDelivery:
        purchaseRequestFlowState.settlement?.advanceCashUponDelivery,
      settlementOption:
        purchaseRequestFlowState.settlement?.settlementOption ?? undefined,
      paymentTermsType: purchaser.paymentTermsPeriod.kind,
      numDaysAfterDelivery:
        purchaser.paymentTermsPeriod.kind === "daysAfterCollection"
          ? purchaser.paymentTermsPeriod.value
          : 0,
      numMonthsFollowingDelivery:
        purchaser.paymentTermsPeriod.kind === "endOfMonth"
          ? purchaser.paymentTermsPeriod.value
          : 0,
    },
    validateOnMount: true,
    validationSchema,
    onSubmit: (values) => {
      const newState: PurchaseRequestFlowState = buildNewFlowState(
        purchaseRequestFlowState,
        ensure(values.advanceCashUponDelivery, {
          msg: "Expected advanceCashUponDelivery to be defined",
        }),
        ensure(values.settlementOption),
        values.paymentTermsType,
        values.numDaysAfterDelivery,
        values.numMonthsFollowingDelivery,
      );
      history.push(AppRoutes.OrganisationPurchaseRequestConfirmation, newState);
    },
  });

  const previewState = useMemo(() => {
    return buildNewFlowState(
      purchaseRequestFlowState,
      form.values.advanceCashUponDelivery ?? false,
      // If the settlement option is not set, default to organisation credit - this doesn't change the calcs
      form.values.settlementOption
        ? form.values.settlementOption
        : "organisationCredit",
      form.values.paymentTermsType,
      form.values.numDaysAfterDelivery,
      form.values.numMonthsFollowingDelivery,
    );
  }, [
    purchaseRequestFlowState,
    form.values.advanceCashUponDelivery,
    form.values.settlementOption,
    form.values.paymentTermsType,
    form.values.numDaysAfterDelivery,
    form.values.numMonthsFollowingDelivery,
  ]);
  const { purchaseRequestData } = usePurchaseRequestPreview(previewState);

  useEffect(() => {
    // If preview still loading or value already set, return early
    if (
      form.values.advanceCashUponDelivery !== undefined ||
      !isLoaded(purchaseRequestData)
    )
      return;

    const discountedReceivable =
      purchaseRequestData.value.summary.discountedReceivable;
    const hasValidAdvance =
      discountedReceivable !== null &&
      Number(discountedReceivable?.netReceivable) >= 0 &&
      Number(discountedReceivable?.amountAdvancedUponDelivery) >= 0;

    form.setFieldValue("advanceCashUponDelivery", hasValidAdvance);
  }, [form, purchaseRequestData]);

  const onBack = () => {
    history.push(
      AppRoutes.OrganisationPurchaseRequestDeliveryOptions,
      purchaseRequestFlowState,
    );
  };

  return (
    <PortalPageContent
      header={
        <PageHeader
          purchaserName={purchaser.name}
          hasProductionPlan={!!purchaseRequestFlowState.productionPlan}
        />
      }
    >
      <Grid container spacing={5}>
        <Grid item xs={12}>
          <OrderSummaryPreviewCard
            purchaseRequestData={purchaseRequestData}
            advanceCashUponDelivery={
              form.values.advanceCashUponDelivery ?? false
            }
            setAdvanceCashUponDelivery={(advanceCashUponDelivery) =>
              form.setFieldValue(
                "advanceCashUponDelivery",
                advanceCashUponDelivery,
              )
            }
          />
        </Grid>
        <Grid item xs={12}>
          <Loader loadingStates={[purchaseRequestData]}>
            {isLoaded(purchaseRequestData) && (
              <SettlementOptionsCard
                disabled={options.isEditMode}
                nominatedPurchaser={purchaser}
                summary={purchaseRequestData.value.summary}
                settlementOption={form.values.settlementOption}
                onSettlementOptionChange={(option) => {
                  form.setFieldValue("settlementOption", option);
                  // invalidate advanceCashUponDelivery if settlement option changes
                  // force the above useEffect to re-run and re-calculate if the value should be set
                  form.setFieldValue("advanceCashUponDelivery", undefined);
                }}
              />
            )}
          </Loader>
        </Grid>
        <Grid item xs={12}>
          <BaseWizardCard
            title={"Payment Date"}
            subtitle={
              "Please specify when payment will be made. Select the latest date the payment must be made by."
            }
            inputElement={
              <PaymentTermsInput form={form} disabled={options.isEditMode} />
            }
          />
        </Grid>
        <Grid item xs={12}>
          <Stack spacing={2} direction="row" justifyContent={"flex-end"}>
            <LoadingButton variant="outlined" onClick={onBack}>
              Back
            </LoadingButton>
            <LoadingButton
              variant="contained"
              onClick={form.submitForm}
              disabled={!form.isValid}
            >
              Next
            </LoadingButton>
          </Stack>
        </Grid>
      </Grid>
    </PortalPageContent>
  );
};

const validationSchema = object().shape({
  ...paymentTermsValidationSchema,
  settlementOption: string().oneOf(valuesTradeSaleSettlementMethod).required(),
});
